import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { listTaxons } from '../api/taxons'
import type { ApiStorefrontCatalog, ApiTaxon } from '../api/types'
import { QUERY_KEYS } from '../constants/query'
import { SHOP_META } from '../constants/shop'
import { toApiMessage } from '../helpers/api-error'
import {
  filterShopProducts,
  findTaxonBySlug,
  isExpressShopSlug,
  shopTitleFromTaxon,
  storefrontProducts,
} from '../helpers/catalog'
import { mapProductCard, type CatalogCard } from '../helpers/mapCatalog'
import { useStorefrontCatalog } from './useStorefrontCatalog'

export type ShopCatalogFilterOptions = {
  sort?: string
}

type ShopCatalogState = {
  title: string
  products: CatalogCard[]
  taxons: ApiTaxon[]
  loading: boolean
  error: string | null
  missingCategory: boolean
}

/**
 * Category products the shopper can order at their location: PAN India items
 * everywhere, local items only where a nearby vendor stocks them.
 */
export function useShopCatalog(
  taxonSlug: string,
  filters?: ShopCatalogFilterOptions,
): ShopCatalogState {
  const { catalog, loading: catalogLoading, error: catalogError } = useStorefrontCatalog()
  const taxonsQuery = useQuery({
    queryKey: QUERY_KEYS.taxons(true),
    queryFn: () => listTaxons(true),
    enabled: Boolean(taxonSlug),
  })

  return useMemo(() => {
    if (!taxonSlug) {
      return {
        title: SHOP_META.fallbackTitle,
        products: [],
        taxons: [],
        loading: false,
        error: null,
        missingCategory: true,
      }
    }

    const error = catalogError
      ? catalogError
      : taxonsQuery.error
        ? toApiMessage(taxonsQuery.error, 'Failed to load products')
        : null
    const loading = catalogLoading || (taxonsQuery.isPending && !taxonsQuery.data)
    const taxons = taxonsQuery.data ?? []
    const taxon = findTaxonBySlug(taxons, taxonSlug)
    const rawRows = catalog ? visibleShopProducts(catalog, taxonSlug) : []
    let rows = filterShopProducts(rawRows, taxonSlug, taxons)

    if (filters?.sort) {
      rows = [...rows]
      if (filters.sort === 'price-asc') {
        rows.sort((a, b) => a.displayPrice - b.displayPrice)
      } else if (filters.sort === 'price-desc') {
        rows.sort((a, b) => b.displayPrice - a.displayPrice)
      } else if (filters.sort === 'newest') {
        rows.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
      } else if (filters.sort === 'name') {
        rows.sort((a, b) => a.name.localeCompare(b.name))
      }
    }

    return {
      title: shopTitleFromTaxon(taxon, taxonSlug),
      products: rows.map(mapProductCard),
      taxons,
      loading: loading && !error,
      error,
      missingCategory: Boolean(taxonsQuery.data && !taxon),
    }
  }, [
    taxonSlug,
    catalog,
    catalogError,
    catalogLoading,
    taxonsQuery.data,
    taxonsQuery.error,
    taxonsQuery.isPending,
    filters?.sort,
  ])
}

function visibleShopProducts(catalog: ApiStorefrontCatalog, taxonSlug: string) {
  if (isExpressShopSlug(taxonSlug)) {
    return catalog.inRange.filter((product) => product.expressAvailable)
  }
  return storefrontProducts(catalog)
}
